import {
  computed,
  nextTick,
  onMounted,
  readonly,
  ref,
  watch,
  type Ref,
} from "vue";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";

export type RangeChangePayload = { value: SelectionRange | null; event: any };

type Part = "mm" | "dd" | "yyyy";

type Side = "start" | "end";

export function useMaskedDateRangeInput(args: {
  id: string; // id of the MaskedTextBox input element
  onChange: (p: RangeChangePayload) => void;
  onShowCalendar: (e: KeyboardEvent) => void;
  externalValue?: Ref<SelectionRange | null | undefined>;
  externalValid?: Ref<boolean | undefined>;
  manageValid?: boolean; // default true: mirror computed validity into externalValid
  maxSpanDays?: number; // optional maximum allowed span between start and end (in days)
  min?: Date;
  max?: Date;
  allowReverse?: boolean; // default false
  // Popup control hooks
  isOpen?: Ref<boolean>;
  onRequestClose?: () => void;
  closeDelay?: Ref<number> | number;
  debug?: boolean;
}) {
  const raw = ref("");
  const cursorPos = ref<number | undefined>(undefined);
  const debugEnabled = ref<boolean>(!!args.debug);
  const calendarChanged = ref<boolean>(false);

  const ONE_DAY = 24 * 60 * 60 * 1000;
  const ONE_YEAR = 365 * ONE_DAY;
  const TODAY = new Date();
  const MIN_DATE = new Date(TODAY.getTime() - ONE_YEAR);
  const MAX_DATE = new Date(TODAY.getTime() + ONE_YEAR);

  args.min = args.min ?? MIN_DATE;
  args.max = args.max ?? MAX_DATE;

  // Define helpers early to avoid "used before initialization" issues
  const digitsOnly = computed(() => (raw.value ?? "").replace(/\D/g, ""));
  const daysInMonth = (yy: number, mm: number) => new Date(yy, mm, 0).getDate();
  const isValidParts = (yy: number, mm: number, dd: number) => {
    return (
      mm >= 1 && mm <= 12 && yy >= 1000 && dd >= 1 && dd <= daysInMonth(yy, mm)
    );
  };
  // Compare dates by date-only (UTC midnight) to avoid time-of-day issues
  const toUtcMidnightMs = (d: Date) =>
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const clampToMinMax = (d: Date | null): Date | null => {
    if (!d) return null;
    // default to 90days ago if min/max not provided
    const min = args.min ?? new Date(Date.now() - 90 * ONE_DAY);
    // default to today
    const max = args.max ?? new Date();
    const dMs = toUtcMidnightMs(d);
    if (dMs < toUtcMidnightMs(min) || dMs > toUtcMidnightMs(max)) return null;
    return d;
  };
  const rangeIsOrdered = (start: Date, end: Date) => {
    return (args.allowReverse ?? false) ? true : start <= end;
  };
  const maskComplete = computed(() => {
    return (raw.value?.length ?? 0) >= 23 && digitsOnly.value.length >= 16;
  });

  const parseFromRaw = (
    s: string,
  ): { start: Date | null; end: Date | null } => {
    // Expect MM/DD/YYYY - MM/DD/YYYY
    if ((s ?? "").length < 23 || digitsOnly.value.length < 16)
      return { start: null, end: null };
    const left = s.substring(0, 10);
    const right = s.substring(13, 23);
    const [mm1, dd1, yyyy1] = left
      .split("/")
      .map((x) => parseInt(x || "0", 10));
    const [mm2, dd2, yyyy2] = right
      .split("/")
      .map((x) => parseInt(x || "0", 10));
    const sDate = new Date(yyyy1, mm1 - 1, dd1);
    const eDate = new Date(yyyy2, mm2 - 1, dd2);
    const sOk =
      isValidParts(yyyy1, mm1, dd1) && sDate.toString() !== "Invalid Date";
    const eOk =
      isValidParts(yyyy2, mm2, dd2) && eDate.toString() !== "Invalid Date";
    return { start: sOk ? sDate : null, end: eOk ? eDate : null };
  };

  // validity state shared with consumer when provided
  // Initialize to true to avoid showing validation errors before first blur
  const valid = args.externalValid ?? ref<boolean | undefined>(true);
  // Derived validity and reason
  const parsed = computed(() => parseFromRaw(raw.value));
  const clamped = computed(() => {
    const s = clampToMinMax(parsed.value.start);
    const e = clampToMinMax(parsed.value.end);
    return { start: s, end: e };
  });

  // Span in days between clamped start/end; undefined when incomplete or out-of-bounds
  const spanDays = computed<number | undefined>(() => {
    if (!maskComplete.value) return undefined;
    const s = clamped.value.start;
    const e = clamped.value.end;
    if (!s || !e) return undefined;
    const diffMs = Math.abs(toUtcMidnightMs(e) - toUtcMidnightMs(s));
    return Math.round(diffMs / 86400000 + 1); // NOTE: FEATHER-1155
  });

  // Set on first blur/clear; nothing is flagged before the user leaves the field
  const isTouched = ref(false);

  const reasonComputed = computed<string | undefined>(() => {
    if (!isTouched.value) return undefined;

    const { start, end } = parsed.value;
    if (!maskComplete.value) return "incomplete";
    if (!start || !end) return "invalid-date";
    if (!clamped.value.start || !clamped.value.end) return "out-of-bounds";
    const s = clamped.value.start;
    const e = clamped.value.end;
    if (!rangeIsOrdered(s, e)) return "reversed-range";
    if (typeof args.maxSpanDays === "number" && spanDays.value !== undefined) {
      if (spanDays.value > args.maxSpanDays) return "span-exceeds-limit";
    }
    return "valid";
  });

  const validComputed = computed<boolean | undefined>(() => {
    const r = reasonComputed.value;
    return r === undefined || r === "valid";
  });

  // User-facing validation message derived from the same live reason as validComputed
  const validationMessage = computed<string>(() => {
    const { min, max } = args;
    const r = reasonComputed.value;

    if (!r) return "";

    switch (r) {
      case "incomplete":
        return "Required";
      case "invalid-date":
        return "One or both dates invalid.";
      case "reversed-range":
        return "End Date must be on or after Start Date.";
      case "out-of-bounds":
        if (min && max) {
          return `Dates must be between ${formatDate(min)} and ${formatDate(max)}.`;
        }
        return "Dates must be within the allowed range.";
      case "span-exceeds-limit":
        return typeof args.maxSpanDays === "number"
          ? `Date range exceeds ${args.maxSpanDays} days.`
          : "Date range exceeds maximum allowed span.";
      case "valid":
      default:
        return "";
    }
  });
  // // Mirror computed validity into external ref when enabled
  // watch(
  //   validComputed,
  //   (v) => {
  //     if (args.manageValid ?? true) {
  //       valid.value = v;
  //     }
  //   },
  //   { immediate: true },
  // );

  const steppers: string[] = ["ArrowUp", "ArrowDown"];
  const cursorKeys: string[] = ["ArrowLeft", "ArrowRight"]; // tracked to keep caret

  const fmt = (n: number, w: number) => String(n).padStart(w, "0");
  const formatDate = (d: Date) =>
    `${fmt(d.getMonth() + 1, 2)}/${fmt(d.getDate(), 2)}/${fmt(
      d.getFullYear(),
      4,
    )}`;

  // rangeIsOrdered declared earlier

  const datePartForPos = (pos: number): { side: Side; part: Part } => {
    // Mask: 00/00/0000 - 00/00/0000
    // Index map (digits start at 0..9 for start, 13..22 for end)
    if (pos <= 2) return { side: "start", part: "mm" };
    if (pos <= 5) return { side: "start", part: pos <= 3 ? "dd" : "dd" };
    if (pos <= 11) return { side: "start", part: "yyyy" };
    if (pos <= 15) return { side: "end", part: "mm" };
    if (pos <= 18) return { side: "end", part: "dd" };
    return { side: "end", part: "yyyy" };
  };

  const applyStep = (
    direction: "ArrowUp" | "ArrowDown",
    sourceEl: HTMLInputElement | null | undefined,
    event: Event,
  ): void => {
    const pos = sourceEl?.selectionStart ?? cursorPos.value ?? 0;
    cursorPos.value = pos;

    // Current parsed values
    const { start, end } = parseFromRaw(raw.value);

    // If SelectionRange is null/undefined (both sides missing), default to today and ignore the step
    if (!start && !end) {
      const today = new Date();
      const tStr = formatDate(today);
      raw.value = `${tStr} - ${tStr}`;
      args.onChange({ value: { start: today, end: today }, event });
      nextTick(() => {
        requestAnimationFrame(() => {
          const inputEl = document.getElementById(
            args.id,
          ) as HTMLInputElement | null;
          const p = cursorPos.value ?? 0;
          if (inputEl) {
            inputEl.focus();
            inputEl.setSelectionRange(p, p);
          }
        });
      });
      return;
    }

    // Build baseline from user's parsed input; clamp only when emitting
    const today = new Date();

    const which = datePartForPos(pos);

    // Baseline dates: prefer parsed values (even if out-of-bounds); otherwise today
    let s = start ? new Date(start) : new Date(today);
    let e = end ? new Date(end) : new Date(today);

    const mutate = (d: Date, part: Part) => {
      if (part === "mm") {
        const m = d.getMonth() + 1;
        const next =
          direction === "ArrowUp" ? (m < 12 ? m + 1 : 1) : m > 1 ? m - 1 : 12;
        d.setMonth(next - 1);
        // Fix day overflow after month change
        const days = daysInMonth(d.getFullYear(), next);
        if (d.getDate() > days) d.setDate(days);
      } else if (part === "dd") {
        const days = daysInMonth(d.getFullYear(), d.getMonth() + 1);
        const day = d.getDate();
        const next =
          direction === "ArrowUp"
            ? day < days
              ? day + 1
              : 1
            : day > 1
              ? day - 1
              : days;
        d.setDate(next);
      } else if (part === "yyyy") {
        const y = d.getFullYear();
        d.setFullYear(direction === "ArrowUp" ? y + 1 : Math.max(1, y - 1));
        // Fix Feb 29 overflow, etc.
        const days = daysInMonth(d.getFullYear(), d.getMonth() + 1);
        if (d.getDate() > days) d.setDate(days);
      }
    };

    if (which.side === "start") mutate(s, which.part);
    else mutate(e, which.part);

    // Compose new raw string: keep the untouched side as-is from current raw if parseable
    const sText = formatDate(s);
    const eText = formatDate(e);
    raw.value = `${sText} - ${eText}`;

    // Emit if valid and ordered and within min/max; else null
    const sClamped = clampToMinMax(s);
    const eClamped = clampToMinMax(e);
    if (sClamped && eClamped && rangeIsOrdered(sClamped, eClamped)) {
      args.onChange({ value: { start: sClamped, end: eClamped }, event });
    } else {
      args.onChange({ value: null, event });
    }

    // Restore caret
    nextTick(() => {
      requestAnimationFrame(() => {
        const inputEl = document.getElementById(
          args.id,
        ) as HTMLInputElement | null;
        const p = cursorPos.value ?? 0;
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(p, p);
        }
      });
    });
  };

  const emitParsedIfComplete = (event: any) => {
    const { start, end } = parseFromRaw(raw.value);
    if (!start || !end) {
      args.onChange({ value: null, event });
      return;
    }
    const sClamped = clampToMinMax(start);
    const eClamped = clampToMinMax(end);
    if (!sClamped || !eClamped) {
      args.onChange({ value: null, event });
      return;
    }
    if (!rangeIsOrdered(sClamped, eClamped)) {
      args.onChange({ value: null, event });
      return;
    }
    args.onChange({ value: { start: sClamped, end: eClamped }, event });
  };

  const handleChange = (event: any): void => {
    const newValue = (event.value ?? "") as string;
    const originalEvent = event.event as Event | undefined;

    // No clear event handler from Kendo...
    // Kendo's clear button sets the value to empty and raises change;
    // treat that as an explicit clear action.
    if (!newValue && originalEvent && (originalEvent as any).type === "click") {
      handleClear(originalEvent);
      return;
    }

    raw.value = newValue;
    const src = originalEvent?.target as HTMLInputElement;
    cursorPos.value = src?.selectionStart ?? 0;
    // Do not coerce user's input; only emit parsed range when complete and valid
    emitParsedIfComplete(event);
  };

  const handleClick = (event: MouseEvent): void => {
    cursorPos.value = (event.target as HTMLInputElement).selectionStart ?? 0;
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    // Space already opens calendar
    if (event.code === "Space" || event.key === " ") {
      // Do not let Space modify the input; only open the calendar
      // Justified: Space opens the calendar popup instead of inserting a space.
      event.preventDefault();
      // Justified: Parent containers such as grid keyboard handlers should not consume this.
      event.stopPropagation();
      args.onShowCalendar(event);
      return;
    }

    // Alt + ArrowDown: open calendar popup without changing the value via stepper
    if (event.key === "ArrowDown" && event.altKey) {
      // Justified: Alt+Down is reserved for opening the calendar popup.
      event.preventDefault();
      // Justified: Prevent parent keyboard navigation from treating this as row movement.
      event.stopPropagation();
      args.onShowCalendar(event);
      return;
    }

    if (steppers.includes(event.key)) {
      // Justified: ArrowUp/Down act as range-part steppers instead of native caret/page behavior.
      event.preventDefault();
      // Justified: Prevent parent containers such as the grid from also handling these arrows.
      event.stopPropagation();
      const src = event.target as HTMLInputElement;
      applyStep(event.key as "ArrowUp" | "ArrowDown", src, event);
      return;
    }
  };

  const handleWheel = (event: WheelEvent): void => {
    // Prevent page scroll when interacting with the input
    event.preventDefault();
    const direction = event.deltaY < 0 ? "ArrowUp" : "ArrowDown";
    applyStep(direction, event.target as HTMLInputElement, event);
  };

  const handleKeyUp = (event: KeyboardEvent): void => {
    if (cursorKeys.includes(event.key) || steppers.includes(event.key)) {
      const src = event.target as HTMLInputElement;
      cursorPos.value = src?.selectionStart ?? 0;
    }
  };

  const applyBlurValidation = () => {
    isTouched.value = true;

    if (args.manageValid ?? true) {
      valid.value = validComputed.value;
    }
  };

  const handleBlur = (_event: FocusEvent): void => {
    applyBlurValidation();
  };

  const handleClear = (event?: Event): void => {
    raw.value = "";
    cursorPos.value = 0;

    // Emit cleared range to consumer
    args.onChange({ value: null, event });

    // Run the same validation flow as on blur
    applyBlurValidation();

    // Return focus to the masked input so the user can retype
    nextTick(() => {
      const inputEl = document.getElementById(
        args.id,
      ) as HTMLInputElement | null;
      if (inputEl) {
        inputEl.focus();
        inputEl.setSelectionRange(0, 0);
      }
    });
  };

  // Sync external range into raw when provided
  if (args.externalValue) {
    watch(
      args.externalValue,
      (newVal) => {
        const start = newVal?.start ?? null;
        const end = newVal?.end ?? null;
        if (start && end) {
          raw.value = `${formatDate(start)} - ${formatDate(end)}`;
          // If the change originated from calendar and popup is open, request close
          if (
            args.isOpen?.value &&
            calendarChanged.value &&
            args.onRequestClose
          ) {
            const delay =
              typeof args.closeDelay === "number"
                ? args.closeDelay
                : (args.closeDelay?.value ?? 0);
            setTimeout(() => {
              args.onRequestClose?.();
              calendarChanged.value = false;
              applyBlurValidation();
            }, delay);
          }
          return;
        }
        // If external is null/invalid, keep current raw as-is to preserve user's typing
      },
      { immediate: true },
    );
  }

  const month1 = computed(() =>
    raw.value ? raw.value.substring(0, 2).replace(/\D/g, "0") || "0" : "0",
  );
  const day1 = computed(() =>
    raw.value ? raw.value.substring(3, 5).replace(/\D/g, "0") || "0" : "0",
  );
  const year1 = computed(() =>
    raw.value ? raw.value.substring(6, 10).replace(/\D/g, "0") || "0" : "0",
  );
  const month2 = computed(() =>
    raw.value ? raw.value.substring(13, 15).replace(/\D/g, "0") || "0" : "0",
  );
  const day2 = computed(() =>
    raw.value ? raw.value.substring(16, 18).replace(/\D/g, "0") || "0" : "0",
  );
  const year2 = computed(() =>
    raw.value ? raw.value.substring(19, 23).replace(/\D/g, "0") || "0" : "0",
  );

  const debugLines = computed((): Array<{ label: string; value: string }> => {
    const { start, end } = parseFromRaw(raw.value);
    return [
      {
        label: "Raw",
        value: raw.value,
      },
      {
        label: "Min Date",
        value: `${args.min ? args.min.toDateString() : "-"}`,
      },
      {
        label: "Max Date",
        value: `${args.max ? args.max.toDateString() : "-"}`,
      },
      {
        label: "Start",
        value: `${String(month1.value).padStart(2, "0")} / ${String(
          day1.value,
        ).padStart(2, "0")} / ${String(year1.value).padStart(4, "0")}`,
      },
      {
        label: "End",
        value: `${String(month2.value).padStart(2, "0")} / ${String(
          day2.value,
        ).padStart(2, "0")} / ${String(year2.value).padStart(4, "0")}`,
      },
      { label: "Digits only", value: digitsOnly.value },
      { label: "Mask complete", value: `${maskComplete.value}` },
      {
        label: "Parsed",
        value: `${start ? start.toDateString() : "-"} | ${
          end ? end.toDateString() : "-"
        }`,
      },
      {
        label: "Valid (managed)",
        value: `${valid.value ?? "-"}`,
      },
      { label: "Span (days)", value: `${spanDays.value ?? "-"}` },
      {
        label: "Reason",
        value: `${reasonComputed.value ?? "-"}`,
      },
      {
        label: "Cursor in",
        value: `${cursorPos.value ?? "-"} (${(() => {
          const p = cursorPos.value ?? 0;
          const w = datePartForPos(p);
          return `${w.side}.${w.part}`;
        })()})`,
      },
    ];
  });

  const initStyling = () => {
    // Idempotent application of the `fk-daterangepicker` class to the input's parent
    nextTick(() => {
      const rangeInput = document.getElementById(args.id) as HTMLElement | null;
      if (!rangeInput) {
        console.warn(`#${args.id} not found for styling.`);
        return;
      }
      const parentDiv = rangeInput.closest("div");
      if (!parentDiv) {
        console.warn(`Parent div of #${args.id} not found for styling.`);
        return;
      }
      if (parentDiv.classList.contains("fk-daterangepicker")) return;
      parentDiv.classList.add("fk-daterangepicker");
    });
  };

  onMounted(() => {
    initStyling();
  });

  // Accessibility fix: ensure only one month grid has tabIndex=0
  const calendarMonthTabIndexFix = () => {
    nextTick(() => {
      try {
        const containers = Array.from(
          document.querySelectorAll(".k-animation-container"),
        ) as HTMLElement[];
        if (!containers.length) return;

        // Prefer the most recently added visible container that has a calendar
        const candidate = [...containers]
          .reverse()
          .find((c) => c.querySelector(".k-calendar"));
        const calendarRoot = candidate?.querySelector(
          ".k-calendar",
        ) as HTMLElement | null;
        if (!calendarRoot) return;

        const months = calendarRoot.querySelectorAll(
          ".k-calendar-table",
        ) as NodeListOf<HTMLElement>;
        let foundTab0 = false;
        months.forEach((m) => {
          if (m.tabIndex === 0) {
            if (!foundTab0) {
              foundTab0 = true;
            } else {
              m.tabIndex = -1;
            }
          }
        });
      } catch (e) {
        // no-op; defensive
        console.warn(e);
      }
    });
  };

  // When popup opens, run the fix after render
  if (args.isOpen) {
    watch(
      args.isOpen,
      (open) => {
        if (open) {
          // Allow popup DOM to mount first
          setTimeout(() => calendarMonthTabIndexFix(), 0);
        }
      },
      { immediate: false },
    );
  }

  return {
    raw,
    cursorPos,
    debugEnabled: debugEnabled,
    debugLines: debugLines,
    digitsOnly: digitsOnly,
    valid,
    validComputed: readonly(validComputed),
    reason: readonly(reasonComputed),
    validationMessage: readonly(validationMessage),
    spanDays: readonly(spanDays),
    month1: readonly(month1),
    day1: readonly(day1),
    year1: readonly(year1),
    month2: readonly(month2),
    day2: readonly(day2),
    year2: readonly(year2),
    handleChange,
    handleKeyDown,
    handleWheel,
    handleKeyUp,
    handleClick,
    handleBlur,
    handleClear,
    onCalendarChange: () => {
      calendarChanged.value = true;
    },
    initStyling,
  };
}
